import { StyledNewProductForm } from './style';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { MansLauksProduct } from 'ui/Drawer/Drawers/DrawerMansLauksProducts';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { Flex, Form } from 'antd';
import {
  Select,
  SelectOption,
  DatePicker,
  InputNumber,
  Button,
  Label,
  Divider,
} from 'ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons';
import { useWatch } from 'antd/lib/form/Form';
import { Rule } from 'rc-field-form/lib/interface';

export interface unitTypes {
  id: number;
  name: string;
  short: string;
  type: string;
  status: string;
  extension: boolean;
}

export interface WholesalePriceRangeProps {
  amount_from?: number | null;
  amount_to?: number | null;
  price?: number | null;
}

export interface WholesaleReserveProps {
  amount_from?: number | null;
  amount_to?: number | null;
  date_from?: Dayjs | null;
  date_to?: Dayjs | null;
}

export const isWholesaleRowValueFilled = (
  value: number | string | Dayjs | null | undefined
) => value !== null && value !== undefined && value !== '';

interface NewProductWholesaleProps {
  mansLauksSelectedProduct?: MansLauksProduct;
}

export const NewProductWholesale = ({
  mansLauksSelectedProduct,
}: NewProductWholesaleProps) => {
  const form = useFormInstance();
  const selectedUnit = useWatch(['wholesale', 'storage_unit'], form);

  const intl = useIntl();

  const { data } = useQueryApiClient({
    request: {
      url: 'api/v2/products/form/attributes',
    },
  });

  const UnitSuffix = ({ withPrice = true }) => {
    return selectedUnit ? (
      <div className={'unit-suffix'}>
        <div>
          {withPrice ? 'Є' : ''}/{selectedUnit}
        </div>
      </div>
    ) : (
      <></>
    );
  };

  const MalaSuffix = () => {
    return mansLauksSelectedProduct ? (
      <div className={'unit-suffix mala'}>
        <div>{`${intl.formatMessage({ id: 'products.mans_lauks_amount' })} ${
          mansLauksSelectedProduct.amount
        }`}</div>
      </div>
    ) : (
      <></>
    );
  };

  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf('day');
  };

  const priceRangeRequired = (rowName: number): Rule => ({
    validator: async (_, value) => {
      const row: WholesalePriceRangeProps =
        form.getFieldValue(['wholesale', 'wholesale_price_ranges', rowName]) ??
        {};
      const rowHasData =
        isWholesaleRowValueFilled(row.amount_from) ||
        isWholesaleRowValueFilled(row.amount_to) ||
        isWholesaleRowValueFilled(row.price);

      if (rowHasData && !isWholesaleRowValueFilled(value)) {
        return Promise.reject(
          new Error(intl.formatMessage({ id: 'validation.required' }))
        );
      }
    },
  });

  const reserveAmountRequired = (rowName: number): Rule => ({
    validator: async (_, value) => {
      const row: WholesaleReserveProps =
        form.getFieldValue(['wholesale', 'wholesale_reserves', rowName]) ?? {};
      const rowHasData =
        isWholesaleRowValueFilled(row.amount_from) ||
        isWholesaleRowValueFilled(row.amount_to) ||
        isWholesaleRowValueFilled(row.date_from) ||
        isWholesaleRowValueFilled(row.date_to);

      if (rowHasData && !isWholesaleRowValueFilled(value)) {
        return Promise.reject(
          new Error(intl.formatMessage({ id: 'validation.required' }))
        );
      }
    },
  });
  const isPackage = selectedUnit !== 'iep';
  const unitTypes: unitTypes[] = data && data['storageUnits'];
  const extensionUnitTypes =
    unitTypes &&
    unitTypes.filter((value) => {
      return value.extension === true;
    });

  return (
    <StyledNewProductForm>
      <div className={'unit-step col-djfgr'}>
        <div className={'unit'}>
          <Select
            showSearch={false}
            className={'scnd-tr'}
            name={['wholesale', 'storage_unit']}
            label={intl.formatMessage({ id: 'general.unit' })}
            validations={'required'}
            children={
              unitTypes &&
              unitTypes.map((unit, index) => {
                return (
                  <SelectOption value={unit.short} key={`${index} - opt`}>
                    {unit.name} {unit.short.length > 0 ? `(${unit.short})` : ''}
                  </SelectOption>
                );
              })
            }
          />
        </div>
        <div className={'step'}>
          <InputNumber
            className={'scnd-tr'}
            name={['wholesale', 'step']}
            label={intl.formatMessage({ id: 'general.step' })}
            validations={['required', 'correctStepForUnit']}
            controls={false}
          />
        </div>
      </div>
      {!isPackage && (
        <div className={'amount-merv col-djfgr'}>
          <div className={'amount'}>
            <InputNumber
              validations={['required']}
              className={'scnd-tr'}
              disabled={isPackage}
              name={['wholesale', 'package_unit_val']}
              label={intl.formatMessage({ id: 'general.amount' })}
              controls={false}
            />
          </div>
          <div className={'merv'}>
            <Select
              validations={'required'}
              className={'scnd-tr'}
              disabled={isPackage}
              name={['wholesale', 'package_unit']}
              label={intl.formatMessage({ id: 'general.unit' })}
              children={
                extensionUnitTypes &&
                extensionUnitTypes.map((unit, index) => {
                  return (
                    <SelectOption value={unit.short} key={`${index} - opt`}>
                      {unit.name}
                      {unit.short.length > 0 ? `(${unit.short})` : ''}
                    </SelectOption>
                  );
                })
              }
            />
          </div>
        </div>
      )}
      <div className={'minamount-maxamount col-djfgr'}>
        <div className={'available-amount'}>
          <InputNumber
            suffix={<MalaSuffix />}
            className={'flex'}
            validations={['required', 'minZeroNumber']}
            controls={false}
            name={['wholesale', 'storage_stock_count']}
            label={intl.formatMessage({ id: 'general.availability_amount' })}
          />
        </div>
        <div className={'minamount'}>
          <InputNumber
            className={'flex'}
            controls={false}
            name={['wholesale', 'storage_limit_min']}
            label={intl.formatMessage({
              id: 'general.min_amount_one_purchase',
            })}
            onChange={() => {
              form.validateFields(['storage_limit_max']);
            }}
            validations={'minLimitNotGreaterThanMax'}
          />
        </div>
        <div className={'maxamount'}>
          <InputNumber
            className={'flex'}
            controls={false}
            onChange={() => {
              form.validateFields(['storage_limit_min']);
            }}
            name={['wholesale', 'storage_limit_max']}
            label={intl.formatMessage({ id: 'general.max_amount' })}
            validations={'minLimitNotGreaterThanMax'}
          />
        </div>
      </div>
      <div className={'price-discount-time col-djfgr'}>
        <div className={'price'}>
          <InputNumber
            className={'flex'}
            suffix={<UnitSuffix />}
            validations={[
              'required',
              'minZeroNumber',
              'priceGreaterThanDiscount',
            ]}
            name={['wholesale', 'storage_stock_price']}
            label={intl.formatMessage({ id: 'product.price_vat' })}
            controls={false}
          />
        </div>
        <div className={'discount'}>
          <InputNumber
            className={'flex'}
            suffix={<UnitSuffix />}
            name={['wholesale', 'storage_stock_discount_price']}
            label={intl.formatMessage({ id: 'product.discount_price_vat' })}
            controls={false}
          />
        </div>
        <div className={'time'}>
          <DatePicker
            className={'flex'}
            name={['wholesale', 'discount_expiration_date']}
            label={intl.formatMessage({ id: 'general.discount_time' })}
            disabledDate={disabledDate}
            disabled={true} // Jauns ips "Jauna funkcionalitāte"
          />
        </div>
      </div>
      <Divider marginTop={0} />
      <Label
        label={intl.formatMessage({ id: 'product.price_according_to_amount' })}
        title
      />
      <Form.List name={['wholesale', 'wholesale_price_ranges']}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Flex
                key={key}
                gap={20}
                justify={'space-between'}
                className={'dynamic-range-row'}
              >
                <InputNumber
                  className={'amount-input'}
                  name={[name, 'amount_from']}
                  label={intl.formatMessage({ id: 'product.amount_from' })}
                  suffix={<UnitSuffix withPrice={false} />}
                  controls={false}
                  min={0}
                  step={1}
                  rules={[priceRangeRequired(name)]}
                />
                <InputNumber
                  className={'amount-input'}
                  name={[name, 'amount_to']}
                  label={intl.formatMessage({ id: 'product.amount_to' })}
                  suffix={<UnitSuffix withPrice={false} />}
                  controls={false}
                  min={0}
                  step={1}
                  rules={[priceRangeRequired(name)]}
                />
                <InputNumber
                  className={'amount-input'}
                  name={[name, 'price']}
                  label={intl.formatMessage({ id: 'product.price' })}
                  suffix={<UnitSuffix withPrice={false} />}
                  controls={false}
                  min={0}
                  rules={[priceRangeRequired(name)]}
                />
                <Form.Item label={' '} className={'remove-button-item'}>
                  <div className="remove-button" onClick={() => remove(name)}>
                    <FontAwesomeIcon icon={faMinus} />
                  </div>
                </Form.Item>
              </Flex>
            ))}
            <Button
              label={intl.formatMessage({ id: 'product.add_field' })}
              type="dashed"
              onClick={() => add()}
              block
            />
          </>
        )}
      </Form.List>

      <Divider marginTop={0} />
      <Label
        label={intl.formatMessage({ id: 'product.amount_to_reserve' })}
        title
      />
      <Form.List name={['wholesale', 'wholesale_reserves']}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Flex
                key={key}
                gap={20}
                justify={'space-between'}
                className={'dynamic-range-row'}
              >
                <InputNumber
                  className={'amount-input'}
                  name={[name, 'amount_from']}
                  label={intl.formatMessage({ id: 'product.amount_from' })}
                  suffix={<UnitSuffix withPrice={false} />}
                  controls={false}
                  min={0}
                  step={1}
                  rules={[reserveAmountRequired(name)]}
                />
                <InputNumber
                  className={'amount-input'}
                  name={[name, 'amount_to']}
                  label={intl.formatMessage({ id: 'product.amount_to' })}
                  suffix={<UnitSuffix withPrice={false} />}
                  controls={false}
                  min={0}
                  step={1}
                  rules={[reserveAmountRequired(name)]}
                />
                <DatePicker
                  className={'amount-input'}
                  name={[name, 'date_from']}
                  label={intl.formatMessage({ id: 'general.date_from' })}
                />
                <DatePicker
                  className={'amount-input'}
                  name={[name, 'date_to']}
                  label={intl.formatMessage({ id: 'general.date_to' })}
                />

                <Form.Item label={' '} className={'remove-button-item'}>
                  <div className="remove-button" onClick={() => remove(name)}>
                    <FontAwesomeIcon icon={faMinus} />
                  </div>
                </Form.Item>
              </Flex>
            ))}
            <Button
              label={intl.formatMessage({ id: 'product.add_field' })}
              type="dashed"
              onClick={() => add()}
              block
            />
          </>
        )}
      </Form.List>
      <Divider marginTop={0} />
    </StyledNewProductForm>
  );
};
